import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../utils/api';

interface Document {
  id: number;
  title: string;
  description?: string;
}

interface DocumentSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (documentIds: number[]) => void;
  defaultDocumentIds?: number[];
  generatingPdf?: boolean;
}

const DocumentSelectionModal: React.FC<DocumentSelectionModalProps> = ({
  open,
  onClose,
  onGenerate,
  defaultDocumentIds = [],
  generatingPdf = false,
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    new Set(defaultDocumentIds)
  );
  const [includeAll, setIncludeAll] = useState(false);

  // Initialize selectedIds when defaultDocumentIds change
  useEffect(() => {
    if (defaultDocumentIds.length > 0 && Array.isArray(documents) && documents.length > 0) {
      setSelectedIds(new Set(defaultDocumentIds));
      // If all documents are selected, set includeAll to true
      const allDefaultIds = new Set(defaultDocumentIds);
      const allDocIds = new Set(documents.map(d => d.id));
      setIncludeAll(
        defaultDocumentIds.length === documents.length &&
        Array.from(allDefaultIds).every(id => allDocIds.has(id))
      );
    } else if (defaultDocumentIds.length === 0) {
      setSelectedIds(new Set());
      setIncludeAll(false);
    }
  }, [defaultDocumentIds, documents]);

  // Fetch documents when modal opens
  useEffect(() => {
    if (open) {
      fetchDocuments();
    } else {
      // Reset state when modal closes
      setSearchTerm('');
      setError(null);
    }
  }, [open]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      // Use the audit-specific endpoint that returns all documents (including inactive)
      // This matches what the audit dashboard shows
      const response = await api.get('/audit/available_documents/');
      // This endpoint returns a direct array (not paginated)
      const docs = Array.isArray(response.data) ? response.data : [];
      setDocuments(docs);
    } catch (err: any) {
      console.error('Error fetching documents:', err);
      setError(err.response?.data?.error || 'Failed to load documents');
      setDocuments([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Filter documents based on search term
  const filteredDocuments = useMemo(() => {
    // Ensure documents is always an array
    if (!Array.isArray(documents)) {
      return [];
    }
    
    if (!searchTerm.trim()) {
      return documents;
    }
    const searchLower = searchTerm.toLowerCase();
    return documents.filter(
      (doc) =>
        doc.title?.toLowerCase().includes(searchLower) ||
        (doc.description && doc.description.toLowerCase().includes(searchLower))
    );
  }, [documents, searchTerm]);

  const handleToggleAll = () => {
    if (includeAll) {
      setSelectedIds(new Set());
      setIncludeAll(false);
    } else {
      if (Array.isArray(documents) && documents.length > 0) {
        const allIds = new Set(documents.map(d => d.id));
        setSelectedIds(allIds);
        setIncludeAll(true);
      }
    }
  };

  const handleToggleDocument = (documentId: number) => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(documentId)) {
      newSelectedIds.delete(documentId);
    } else {
      newSelectedIds.add(documentId);
    }
    setSelectedIds(newSelectedIds);
    
    // Update includeAll based on selection
    if (Array.isArray(documents) && documents.length > 0) {
      const allDocIds = new Set(documents.map(d => d.id));
      setIncludeAll(newSelectedIds.size === allDocIds.size);
    }
  };

  const handleUseDefaults = () => {
    if (defaultDocumentIds.length > 0 && Array.isArray(documents) && documents.length > 0) {
      setSelectedIds(new Set(defaultDocumentIds));
      const allDocIds = new Set(documents.map(d => d.id));
      setIncludeAll(
        defaultDocumentIds.length === documents.length &&
        Array.from(defaultDocumentIds).every(id => allDocIds.has(id))
      );
    } else {
      setSelectedIds(new Set());
      setIncludeAll(false);
    }
  };

  const handleGenerate = () => {
    onGenerate(Array.from(selectedIds));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' }
      }}
    >
      <DialogTitle>Select Documents to Include in Report</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeAll}
                  onChange={handleToggleAll}
                  indeterminate={selectedIds.size > 0 && selectedIds.size < documents.length}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Include All ({selectedIds.size} of {documents.length} selected)
                </Typography>
              }
            />
            <Button
              size="small"
              onClick={handleUseDefaults}
              disabled={defaultDocumentIds.length === 0}
            >
              Use Default Settings
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredDocuments.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm
                ? 'No documents match your search.'
                : 'No documents available.'}
            </Typography>
          </Box>
        ) : (
          <List
            sx={{
              maxHeight: '400px',
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {filteredDocuments.map((document) => {
              const isSelected = selectedIds.has(document.id);
              return (
                <ListItem key={document.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleToggleDocument(document.id)}
                    dense
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={isSelected}
                        edge="start"
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={document.title}
                      secondary={document.description || 'No description'}
                      primaryTypographyProps={{
                        sx: { fontWeight: isSelected ? 'bold' : 'normal' }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={generatingPdf}>
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={generatingPdf || !Array.isArray(documents) || documents.length === 0}
          startIcon={generatingPdf ? <CircularProgress size={16} /> : null}
        >
          {generatingPdf ? 'Generating...' : 'Generate Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentSelectionModal;
